/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { Form, Checkbox } from 'antd'
import TextArea from 'antd/lib/input/TextArea'
import { FC, useCallback, useEffect, useState } from 'react'
import { Common, CustomFormInstance, ValueOf } from '../../../../types'
import {
    CompositionForm,
    MachineForm,
    MachineNames,
    MachineValues,
    PrimaryAdditionalComponent,
    PrimaryComponent,
} from '../types'
import { connectionRelation, UNITS } from '../../../../const'
import { extractComponentName } from '../../../functions'

type Values = Common<CompositionForm, MachineForm>

type Props = {
    form: CustomFormInstance<Values>
}

let parentClaimPrimaryComponents: PrimaryComponent[] = []
let parentClaimAdditionalComponents: PrimaryAdditionalComponent[] = []

const AbstractOfTheDisclosure: FC<Props> = ({ form }) => {
    const summary = form.getFieldValue<
        Values['summaryOfSelectedEmbodimentsOfTheInvention']
    >('summaryOfSelectedEmbodimentsOfTheInvention')

    // const test = form.getFieldValue<
    //     Values['subSummaryOfSelectedEmbodimentsOfTheInvention']
    // >('subSummaryOfSelectedEmbodimentsOfTheInvention')
    const primaryComponents =
        form.getFieldValue<MachineForm['primaryComponents']>(
            'primaryComponents'
        )
    const additionalComponents = form.getFieldValue<
        MachineForm['primaryAdditionalComponents']
    >(MachineNames.primaryAdditionalComponents)
    const list =
        form.getFieldValue<MachineForm['mainSubcomponents']>(
            MachineNames.mainSubcomponents
        ) ?? []
    const abstractComponents: any[] = []
    const abstractSubComponents: any[] = []
    const abstractFeatures: any[] = []

    if (primaryComponents) {
        parentClaimPrimaryComponents = primaryComponents.filter((item) => {
            if (summary && item !== undefined) {
                return summary.includes(item.id ?? '')
            }

            return false
        })
        parentClaimPrimaryComponents.forEach((comp) => {
            abstractComponents.push(
                comp?.indefiniteArticle === ' '
                    ? `${comp?.component}`
                    : `${comp?.indefiniteArticle} ${comp?.component}`
            )
        })
    }
    if (additionalComponents) {
        parentClaimAdditionalComponents = additionalComponents.filter(
            (item) => {
                if (summary && item !== undefined) {
                    return summary.includes(`add_${item.id}`)
                }

                return false
            }
        )

        parentClaimAdditionalComponents.forEach((comp) => {
            abstractComponents.push(
                comp.indefiniteArticle === ' '
                    ? `${comp.component}`
                    : `${comp.indefiniteArticle} ${comp.component}`
            )
        })
    }

    list.forEach((mainComponent) => {
        const componentData = primaryComponents.find(
            (x) => x?.id === mainComponent
        )

        if (componentData) {
            const subComponents = form.getFieldValue([
                MachineNames.primaryComponentSubComponentsList,
                mainComponent,
            ]) as ValueOf<MachineForm['primaryComponentSubComponentsList']>
            if (subComponents) {
                const primaryComponentData = parentClaimPrimaryComponents.find(
                    (x) => x?.id === mainComponent
                )
                if (primaryComponentData) {
                    const parentClaimSubComponents = subComponents.filter(
                        (item) => {
                            if (summary && item !== undefined) {
                                return summary.includes(
                                    `${componentData.id}+=>${item.id}`
                                )
                            }

                            return false
                        }
                    )
                    if (parentClaimSubComponents.length > 0) {
                        abstractSubComponents.push(
                            `The ${
                                primaryComponentData?.component
                            } comprises ${parentClaimSubComponents
                                .map((subComp: any) =>
                                    subComp.indefiniteArticle === ' '
                                        ? `${subComp.component}`
                                        : `${subComp.indefiniteArticle} ${subComp.component}`
                                )
                                ?.join(', ')
                                .replace(/,(?=[^,]*$)/, ' and')}.`
                        )
                    }
                }
            }
        }
    })

    const characteristicComponents = form.getFieldValue(
        MachineNames.componentsForCharacteristics
    ) as MachineValues['componentsForCharacteristics']
    characteristicComponents.forEach((component: string) => {
        const parentClaimPrimaryComponent = parentClaimPrimaryComponents.find(
            (x) => x?.id === component
        )
        if (parentClaimPrimaryComponent) {
            const characteristic = form.getFieldValue([
                MachineNames.characteristic,
                component,
            ]) as ValueOf<MachineValues['characteristic']>
            const additionalCharacteristic = form.getFieldValue<
                ValueOf<MachineValues['additionalCharacteristic']>
            >(['additionalCharacteristic', component])
            const formatUserDefined = form.getFieldValue<
                ValueOf<MachineValues['formatUserDefined']>
            >(['formatUserDefined', component])
            if (characteristic) {
                Object.entries(characteristic).forEach((entity) => {
                    const [entityKey, val] = entity
                    const key = entityKey as keyof typeof UNITS
                    if (val) {
                        if (
                            summary &&
                            summary.includes(
                                `characteristic_${component}.${key}`
                            )
                        ) {
                            if (typeof val === 'object' && val !== null) {
                                if (key === 'defined') {
                                    abstractFeatures.push(
                                        `The ${
                                            'description' in val
                                                ? val.description
                                                : '__'
                                        }.`
                                    )
                                } else {
                                    abstractFeatures.push(
                                        `The ${parentClaimPrimaryComponent.component!} has a ${key} from ${
                                            'from' in val ? val.from : '__'
                                        } to ${'to' in val ? val.to : '__'} ${
                                            UNITS[key]
                                        }.`
                                    )
                                }
                            }
                        }
                    }
                })
            }
            if (additionalCharacteristic) {
                Object.entries(additionalCharacteristic).forEach((entity) => {
                    const [entityKey, val] = entity
                    const key = entityKey as keyof typeof UNITS
                    if (val) {
                        if (
                            summary &&
                            summary.includes(
                                `additionalCharacteristic_${component}.${key}`
                            )
                        ) {
                            if (typeof val === 'object' && val !== null) {
                                if ('description' in val) {
                                    if (key === 'defined') {
                                        abstractFeatures.push(
                                            `The ${
                                                'description' in val
                                                    ? val.description
                                                    : '__'
                                            }.`
                                        )
                                    }
                                } else {
                                    if (key === 'thermalConductivity') {
                                        abstractFeatures.push(
                                            `The ${
                                                parentClaimPrimaryComponent.component
                                            } has a thermal conductivity from ${
                                                val.from || '__'
                                            } to ${val.to || '__'} ${
                                                UNITS[key]
                                            }.`
                                        )
                                    }
                                    if (key === 'meltingPoint') {
                                        abstractFeatures.push(
                                            `The ${
                                                parentClaimPrimaryComponent.component
                                            } has a melting point from ${
                                                val.from || '__'
                                            } to ${val.to || '__'} ${
                                                UNITS[key]
                                            }.`
                                        )
                                    }
                                    if (key === 'specificHeat') {
                                        abstractFeatures.push(
                                            `The ${
                                                parentClaimPrimaryComponent.component
                                            } has a specific heat from ${
                                                val.from || '__'
                                            } to ${val.to || '__'} ${
                                                UNITS[key]
                                            }.`
                                        )
                                    }
                                }
                            }
                        }
                    }
                })
            }
            if (formatUserDefined) {
                Object.entries(formatUserDefined).forEach((entity: any) => {
                    const [entityKey, val] = entity
                    const key = entityKey
                    if (val) {
                        if (
                            summary &&
                            summary.includes(
                                `formatUserDefined_${component}.${key}`
                            )
                        ) {
                            if (typeof val === 'object' && val !== null) {
                                if (key === 'from') {
                                    abstractFeatures.push(
                                        `The ${parentClaimPrimaryComponent.component!} has a ${
                                            val.name || '__'
                                        } from ${val.minimum || '__'} to ${
                                            val.maximum || '__'
                                        } ${val.units || ''}.`
                                    )
                                }
                                if (key === 'equal') {
                                    abstractFeatures.push(
                                        `The ${
                                            val.name || '__'
                                        } of the ${parentClaimPrimaryComponent.component!} is equal to from ${
                                            val.minimum || '__'
                                        } to ${val.maximum || '__'} ${
                                            val.units || ''
                                        }.`
                                    )
                                }
                                if (key === 'defined') {
                                    abstractFeatures.push(
                                        `The ${val.description || '___'}.`
                                    )
                                }
                            }
                        }
                    }
                })
            }
        }
    })

    if (parentClaimAdditionalComponents) {
        const additionalComponentsForCharacteristics = form.getFieldValue<
            MachineValues['additionalComponentsForCharacteristics']
        >('additionalComponentsForCharacteristics')

        additionalComponentsForCharacteristics?.forEach((component: string) => {
            const parentClaimAdditionalComponent =
                parentClaimAdditionalComponents?.find(
                    (x) => x?.id === component
                )
            if (parentClaimAdditionalComponent) {
                const addCompCharacteristic = form.getFieldValue([
                    MachineNames.addCompCharacteristic,
                    component,
                ]) as ValueOf<MachineValues['addCompCharacteristic']>
                const addCompAdditionalCharacteristic = form.getFieldValue<
                    ValueOf<MachineValues['addCompAdditionalCharacteristic']>
                >(['addCompAdditionalCharacteristic', component])
                const addCompFormatUserDefined = form.getFieldValue<
                    ValueOf<MachineValues['addCompFormatUserDefined']>
                >(['addCompFormatUserDefined', component])
                if (addCompCharacteristic) {
                    Object.entries(addCompCharacteristic).forEach((entity) => {
                        const [entityKey, val] = entity
                        const key = entityKey as keyof typeof UNITS
                        if (val) {
                            if (
                                summary &&
                                summary.includes(
                                    `addCompCharacteristic_${component}.${key}`
                                )
                            ) {
                                if (typeof val === 'object' && val !== null) {
                                    if (key === 'defined') {
                                        abstractFeatures.push(
                                            `The ${
                                                'description' in val
                                                    ? val.description
                                                    : '__'
                                            }.`
                                        )
                                    } else {
                                        abstractFeatures.push(
                                            `The ${parentClaimAdditionalComponent.component!} has a ${key} from ${
                                                'from' in val ? val.from : '__'
                                            } to ${
                                                'to' in val ? val.to : '__'
                                            } ${UNITS[key]}.`
                                        )
                                    }
                                }
                            }
                        }
                    })
                }
                if (addCompAdditionalCharacteristic) {
                    Object.entries(addCompAdditionalCharacteristic).forEach(
                        (entity) => {
                            const [entityKey, val] = entity
                            const key = entityKey as keyof typeof UNITS
                            if (val) {
                                if (
                                    summary &&
                                    summary.includes(
                                        `addCompAdditionalCharacteristic_${component}.${key}`
                                    )
                                ) {
                                    if (
                                        typeof val === 'object' &&
                                        val !== null
                                    ) {
                                        if ('description' in val) {
                                            if (key === 'defined') {
                                                abstractFeatures.push(
                                                    `The ${
                                                        'description' in val
                                                            ? val.description
                                                            : '__'
                                                    }.`
                                                )
                                            }
                                        } else {
                                            if (key === 'thermalConductivity') {
                                                abstractFeatures.push(
                                                    `The ${
                                                        parentClaimAdditionalComponent.component
                                                    } has a thermal conductivity from ${
                                                        val.from || '__'
                                                    } to ${val.to || '__'} ${
                                                        UNITS[key]
                                                    }.`
                                                )
                                            }
                                            if (key === 'meltingPoint') {
                                                abstractFeatures.push(
                                                    `The ${
                                                        parentClaimAdditionalComponent.component
                                                    } has a melting point from ${
                                                        val.from || '__'
                                                    } to ${val.to || '__'} ${
                                                        UNITS[key]
                                                    }.`
                                                )
                                            }
                                            if (key === 'specificHeat') {
                                                abstractFeatures.push(
                                                    `The ${
                                                        parentClaimAdditionalComponent.component
                                                    } has a specific heat from ${
                                                        val.from || '__'
                                                    } to ${val.to || '__'} ${
                                                        UNITS[key]
                                                    }.`
                                                )
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    )
                }
                if (addCompFormatUserDefined) {
                    Object.entries(addCompFormatUserDefined).forEach(
                        (entity: any) => {
                            const [entityKey, val] = entity
                            const key = entityKey
                            if (val) {
                                if (
                                    summary &&
                                    summary.includes(
                                        `addCompFormatUserDefined_${component}.${key}`
                                    )
                                ) {
                                    if (
                                        typeof val === 'object' &&
                                        val !== null
                                    ) {
                                        if (key === 'from') {
                                            abstractFeatures.push(
                                                `The ${parentClaimAdditionalComponent.component!} has a ${
                                                    val.name || '__'
                                                } from ${
                                                    val.minimum || '__'
                                                } to ${val.maximum || '__'} ${
                                                    val.units || ''
                                                }.`
                                            )
                                        }
                                        if (key === 'equal') {
                                            abstractFeatures.push(
                                                `The ${
                                                    val.name || '__'
                                                } of the ${parentClaimAdditionalComponent.component!} is equal to from ${
                                                    val.minimum || '__'
                                                } to ${val.maximum || '__'} ${
                                                    val.units || ''
                                                }.`
                                            )
                                        }
                                        if (key === 'defined') {
                                            abstractFeatures.push(
                                                `The ${
                                                    val.description || '___'
                                                }.`
                                            )
                                        }
                                    }
                                }
                            }
                        }
                    )
                }
            }
        })
    }

    const importantComponentsConnections = form.getFieldValue<
        MachineValues['importantComponentsConnections']
    >(['importantComponentsConnections'])
    if (importantComponentsConnections != null) {
        Object.entries(importantComponentsConnections).forEach((entry: any) => {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const [key, val] = entry
            let finalText: string | undefined
            const notSet = val?.notSet
            if (
                summary &&
                summary.includes(`importantComponentsConnections_${val.id}`)
            ) {
                const firstComponent =
                    val &&
                    summary &&
                    summary.includes(val['1stComponent']) &&
                    extractComponentName(val['1stComponent'], form)
                const secondComponent =
                    val &&
                    summary &&
                    summary.includes(val['2ndComponent']) &&
                    extractComponentName(val['2ndComponent'], form)
                if (notSet) {
                    const firstPart = val?.['1stPart']
                    const secondPart = val?.['2ndPart']
                    const thirdPart = val?.['3rdPart']
                    if (
                        firstPart &&
                        firstComponent &&
                        secondPart &&
                        secondComponent &&
                        thirdPart
                    ) {
                        finalText = `${firstPart} the ${firstComponent} ${secondPart} the ${secondComponent} ${
                            thirdPart || '__'
                        }.`
                    }
                } else {
                    const foundSubConnectionType = connectionRelation[
                        val?.connectionType
                    ]?.find((connection: any) => {
                        return connection.value === val?.subConnectionType
                    })
                    const connectionType = foundSubConnectionType
                        ? val?.subConnectionType || val?.connectionType
                        : val?.connectionType
                    if (firstComponent && connectionType && secondComponent) {
                        finalText = `The ${firstComponent} is ${connectionType.toLowerCase()} the ${secondComponent}.`
                    }
                }
                if (finalText) {
                    abstractFeatures.push(finalText)
                }
            }
        })
    }
    const importantComponentsArrangements = form.getFieldValue<
        MachineValues['importantComponentsArrangements']
    >(['importantComponentsArrangements'])
    if (importantComponentsArrangements != null) {
        Object.entries(importantComponentsArrangements).forEach(
            (entry: any) => {
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                const [key, val] = entry
                let finalText: string | undefined
                const notSet = val?.notSet
                if (
                    summary &&
                    summary.includes(
                        `importantComponentsArrangements_${val.id}`
                    )
                ) {
                    const firstComponent =
                        val &&
                        summary &&
                        summary.includes(val['1stComponent']) &&
                        extractComponentName(val['1stComponent'], form)
                    const secondComponent =
                        val &&
                        summary &&
                        summary.includes(val['2ndComponent']) &&
                        extractComponentName(val['2ndComponent'], form)
                    if (notSet) {
                        const firstPart = val?.['1stPart']
                        const secondPart = val?.['2ndPart']
                        const thirdPart = val?.['3rdPart']
                        if (firstComponent && secondPart && secondComponent) {
                            finalText = `${
                                firstPart ? `${firstPart} the ` : ''
                            }${firstComponent} ${secondPart} the ${secondComponent} ${
                                thirdPart || '__'
                            }.`
                        }
                    } else {
                        const arrangementType = val?.arrangementType
                        if (
                            firstComponent &&
                            arrangementType &&
                            secondComponent
                        ) {
                            finalText = `The ${firstComponent} is ${arrangementType.toLowerCase()} the ${secondComponent}.`
                        }
                    }
                    if (finalText) {
                        abstractFeatures.push(finalText)
                    }
                }
            }
        )
    }
    const functionalConnections = form.getFieldValue<
        MachineValues['functionalConnections']
    >(['functionalConnections'])
    if (functionalConnections != null) {
        Object.entries(functionalConnections).forEach((entry: any) => {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const [key, val] = entry
            let finalText: string | undefined
            const notSet = val?.notSet
            if (
                summary &&
                summary.includes(`functionalConnections_${val.id}`)
            ) {
                const firstComponent =
                    val &&
                    summary &&
                    summary.includes(val['1stComponent']) &&
                    extractComponentName(val['1stComponent'], form)
                const secondComponent =
                    val &&
                    summary &&
                    summary.includes(val['2ndComponent']) &&
                    extractComponentName(val['2ndComponent'], form)
                if (notSet) {
                    const firstPart = val?.['1stPart']
                    const secondPart = val?.['2ndPart']
                    const thirdPart = val?.['3rdPart']
                    if (firstComponent && secondPart && secondComponent) {
                        finalText = `${
                            firstPart ? `${firstPart} the ` : ''
                        }${firstComponent} ${secondPart} the ${secondComponent} ${
                            thirdPart || '__'
                        }.`
                    }
                } else if (firstComponent && secondComponent) {
                    finalText = `The ${firstComponent} is functionally connected with the ${secondComponent}.`
                }
                if (finalText) {
                    abstractFeatures.push(finalText)
                }
            }
        })
    }

    const handleInput = `In a first aspect, the invention comprises a machine. The machine comprises ${abstractComponents
        .join(', ')
        .replace(/,(?=[^,]*$)/, ' and')}. ${abstractSubComponents.join(
        ' '
    )} ${abstractFeatures.join(' ')}`

    const [{ content, wordCount }, setContent] = useState({
        content: handleInput,
        wordCount: 0,
    })

    const setFormattedContent = useCallback(
        (text) => {
            const words = text.split(' ').filter(Boolean)
            if (words.length > 150) {
                setContent({
                    content: words.slice(0, 150).join(' '),
                    wordCount: words.length,
                })
            } else {
                setContent({ content: text, wordCount: words.length })
            }
        },
        [setContent]
    )

    useEffect(() => {
        setFormattedContent(content)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return (
        <>
            {content !== undefined ? (
                <>
                    <Form.Item
                        name={['abstractOfTheDisclosure', 'checked']}
                        rules={[
                            {
                                required: true,
                                transform: (value) => value || undefined,
                                type: 'boolean', // Do the magic
                                message: 'This checkbox is required',
                            },
                        ]}
                        valuePropName="checked"
                    >
                        <Checkbox>
                            The automatically generated abstract of the
                            disclosure is adequate.
                        </Checkbox>
                    </Form.Item>
                    <Form.Item
                        name={['abstractOfTheDisclosure', 'defined']}
                        initialValue={content}
                        rules={[
                            form.getFieldValue([
                                'abstractOfTheDisclosure',
                                'patentProfessionalReview',
                            ])
                                ? {}
                                : {
                                      validator: (_, value) => {
                                          if (!value) return Promise.resolve()
                                          const words = value
                                              .split(' ')
                                              .filter(Boolean)
                                          if (words.length > 150) {
                                              return Promise.reject(
                                                  new Error(
                                                      'Please reduce the abstract to 150 words'
                                                  )
                                              )
                                          }
                                          return Promise.resolve()
                                      },
                                  },
                        ]}
                    >
                        <TextArea
                            onChange={(event) =>
                                setFormattedContent(event.target.value)
                            }
                            value={content}
                        />
                    </Form.Item>
                    <div
                        style={{
                            color: '#b0adab',
                            position: 'absolute',
                            right: 0,
                        }}
                    >
                        {wordCount}/150 words
                    </div>
                    <div style={{ marginBottom: '48px' }} />
                    <Form.Item
                        name={[
                            'abstractOfTheDisclosure',
                            'patentProfessionalReview',
                        ]}
                        valuePropName="checked"
                    >
                        <Checkbox>
                            Ignore abstract length validation & add for patent
                            professional review.
                        </Checkbox>
                    </Form.Item>
                </>
            ) : (
                <></>
            )}
        </>
    )
}

export default AbstractOfTheDisclosure
